import { combineReducers } from 'redux';
import { configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import AppStatusSlice from './slices/AppStatusSlice';
import BasketSlice from './slices/BasketSlice';
import EventHelperSlice from './slices/EventHelperSlice';
import InitialDataSlice from './slices/InitialDataSlice';
import OrderSlice from './slices/OrderSlice';
import PaymentOptionsSlice from './slices/PaymentOptionsSlice';
import ProfileSlice from './slices/ProfileSlice';
import ProductLikesSlice from './slices/ProductLikesSlice';
import MobileAppSlice from './slices/MobileAppSlice';
import MarketingSlice from './slices/MarketingSlice';
import InterceptorMiddleware from './middleware/InterceptorMiddleware';
import RootReducerMiddleware from './middleware/RootReducerMiddleware';
import * as Sentry from '@sentry/react';
import { REDUX_STORE_SLICES } from '../services/exports/Constants';
import PaymentFlowSlice from './slices/PaymentFlowSlice';
import {
  authApiSlice,
  companiesApiSlice,
  featuredPostsApiSlice,
  franchisesApiSlice,
  orderSlotsApiSlice,
  ordersApiSlice,
  profileApiSlice,
  storefrontApiSlice,
} from './api-slices';
import OrderMethodSlice from './slices/OrderMethodSlice';
import { setupListeners } from '@reduxjs/toolkit/query';
import OfferSlice from './slices/OfferSlice';
import { getPreloadedState } from './utils';
import { StoreKeys } from './Constants';

let rehydrationComplete;

const rehydrationPromise = new Promise((resolve) => {
  rehydrationComplete = resolve;
});

export function rehydration() {
  return rehydrationPromise;
}

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    return action.type?.startsWith('initialData/') ? null : action;
  },
  stateTransformer: (state) => ({
    ...state,
    initialData: null,
  }),
});

const persistConfigBlackList = [
  authApiSlice.reducerPath,
  ordersApiSlice.reducerPath,
  profileApiSlice.reducerPath,
  featuredPostsApiSlice.reducerPath,
  orderSlotsApiSlice.reducerPath,
  franchisesApiSlice.reducerPath,
  storefrontApiSlice.reducerPath,
  companiesApiSlice.reducerPath,
  OrderMethodSlice.name,
];

const persistConfig = {
  key: StoreKeys.OnlineShop,
  storage,
  blacklist: persistConfigBlackList,
};

export const reducersCombined = combineReducers({
  [REDUX_STORE_SLICES.INITIAL_DATA]: InitialDataSlice.reducer,
  [REDUX_STORE_SLICES.PROFILE]: ProfileSlice.reducer,
  [REDUX_STORE_SLICES.BASKET]: BasketSlice.reducer,
  [REDUX_STORE_SLICES.ORDER]: OrderSlice.reducer,
  [REDUX_STORE_SLICES.PAYMENT_FLOW]: PaymentFlowSlice.reducer,
  [REDUX_STORE_SLICES.PAYMENT_OPTIONS]: PaymentOptionsSlice.reducer,
  [REDUX_STORE_SLICES.PRODUCT_LIKES]: ProductLikesSlice.reducer,
  [REDUX_STORE_SLICES.APP_STATUS]: AppStatusSlice.reducer,
  [REDUX_STORE_SLICES.EVENT_HELPER]: EventHelperSlice.reducer,
  [REDUX_STORE_SLICES.ORDER_METHOD]: OrderMethodSlice.reducer,
  [REDUX_STORE_SLICES.MARKETING]: MarketingSlice.reducer,
  [REDUX_STORE_SLICES.OFFER]: OfferSlice.reducer,
  [REDUX_STORE_SLICES.MOBILE_APP]: MobileAppSlice.reducer,

  [companiesApiSlice.reducerPath]: companiesApiSlice.reducer,
  [authApiSlice.reducerPath]: authApiSlice.reducer,
  [ordersApiSlice.reducerPath]: ordersApiSlice.reducer,
  [profileApiSlice.reducerPath]: profileApiSlice.reducer,
  [featuredPostsApiSlice.reducerPath]: featuredPostsApiSlice.reducer,
  [orderSlotsApiSlice.reducerPath]: orderSlotsApiSlice.reducer,
  [franchisesApiSlice.reducerPath]: franchisesApiSlice.reducer,
  [storefrontApiSlice.reducerPath]: storefrontApiSlice.reducer,
});

const reducerPersisted = persistReducer(persistConfig, reducersCombined);

const configure = () =>
  configureStore({
    reducer: (state, actions) => RootReducerMiddleware(state, actions, reducerPersisted),
    preloadedState: getPreloadedState(StoreKeys.Storefront),
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      })
        .concat(InterceptorMiddleware)
        .concat(companiesApiSlice.middleware)
        .concat(authApiSlice.middleware)
        .concat(ordersApiSlice.middleware)
        .concat(profileApiSlice.middleware)
        .concat(featuredPostsApiSlice.middleware)
        .concat(orderSlotsApiSlice.middleware)
        .concat(franchisesApiSlice.middleware)
        .concat(storefrontApiSlice.middleware),
    enhancers: [sentryReduxEnhancer],
  });

export const store = configure();
export const persistor = persistStore(store, null, () => {
  // this will be invoked after rehydration is complete
  rehydrationComplete();
});

setupListeners(store.dispatch);
